<template>
  <footer class="footer">
    <div class="container">
      <div class="footer-content">
        <!-- Brand Section -->
        <div class="footer-brand">
          <div class="brand-logo">
            <span class="spark-icon">⚡</span>
            <span class="brand-text">KWIQ PySpark</span>
          </div>
          <p class="brand-description">
            Master Python Spark development with interactive code samples, real-world examples, and comprehensive tutorials.
          </p>
          <div class="footer-links">
            <a href="https://boot.kwiqsolutions.com" target="_blank" class="footer-link">
              Spring Boot Learning Platform
            </a>
            <a href="https://express.kwiqsolutions.com" target="_blank" class="footer-link">
              Express.js Learning Platform
            </a>
            <a href="https://kwiqsolutions.com" target="_blank" class="footer-link">
              KWIQ Solutions Business
            </a>
          </div>
        </div>

        <!-- Quick Links -->
        <div class="footer-section">
          <h4>Quick Links</h4>
          <nav class="footer-nav">
            <RouterLink to="/" class="footer-nav-link">Home</RouterLink>
            <RouterLink to="/samples" class="footer-nav-link">Code Samples</RouterLink>
            <RouterLink to="/about" class="footer-nav-link">About</RouterLink>
          </nav>
        </div>

        <!-- Sample Categories -->
        <div class="footer-section">
          <h4>Sample Categories</h4>
          <nav class="footer-nav">
            <a href="#" class="footer-nav-link">DataFrames</a>
            <a href="#" class="footer-nav-link">Streaming</a>
            <a href="#" class="footer-nav-link">Machine Learning</a>
            <a href="#" class="footer-nav-link">SQL Operations</a>
            <a href="#" class="footer-nav-link">Performance</a>
          </nav>
        </div>

        <!-- Resources -->
        <div class="footer-section">
          <h4>Resources</h4>
          <nav class="footer-nav">
            <a href="https://spark.apache.org/docs/latest/api/python/" target="_blank" class="footer-nav-link">
              PySpark Docs
            </a>
            <a href="https://spark.apache.org/" target="_blank" class="footer-nav-link">
              Apache Spark
            </a>
            <a href="https://github.com/" target="_blank" class="footer-nav-link">
              GitHub
            </a>
            <a href="https://stackoverflow.com/questions/tagged/pyspark" target="_blank" class="footer-nav-link">
              Stack Overflow
            </a>
          </nav>
        </div>
      </div>

      <!-- Footer Bottom -->
      <div class="footer-bottom">
        <div class="footer-bottom-content">
          <p class="copyright">
            © 2025 KWIQ Solutions.
          </p>
        </div>
      </div>
    </div>
  </footer>
</template>

<script setup lang="ts">
import { RouterLink } from 'vue-router'
</script>

<style scoped>
.footer {
  background: linear-gradient(135deg, var(--surface), #f1f5f9);
  border-top: 2px solid var(--primary-color);
  margin-top: auto;
}

.footer-content {
  display: grid;
  grid-template-columns: 2fr 1fr 1fr 1fr;
  gap: 3rem;
  padding: 3rem 0;
}

.footer-brand {
  max-width: 300px;
}

.brand-logo {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 1.5rem;
  font-weight: 700;
  color: var(--primary-color);
  margin-bottom: 1rem;
}

.spark-icon {
  font-size: 1.75rem;
  background: linear-gradient(135deg, var(--primary-color), var(--primary-color-dark));
  background-clip: text;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
}

.brand-text {
  color: var(--text-color);
}

.brand-description {
  color: var(--text-light);
  font-size: 0.9rem;
  line-height: 1.6;
  margin-bottom: 1.5rem;
}

.footer-links {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.footer-link {
  color: var(--primary-color);
  text-decoration: none;
  font-size: 0.875rem;
  font-weight: 500;
  transition: var(--transition);
}

.footer-link:hover {
  color: var(--primary-color-dark);
  text-decoration: underline;
}

.footer-section h4 {
  color: var(--text-color);
  font-size: 1rem;
  font-weight: 600;
  margin-bottom: 1rem;
  border-bottom: 2px solid var(--primary-color);
  padding-bottom: 0.5rem;
  display: inline-block;
}

.footer-nav {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.footer-nav-link {
  color: var(--text-light);
  text-decoration: none;
  font-size: 0.875rem;
  transition: var(--transition);
  padding: 0.25rem 0;
}

.footer-nav-link:hover,
.router-link-active.footer-nav-link {
  color: var(--primary-color);
  transform: translateX(4px);
}

.footer-bottom {
  border-top: 1px solid var(--border);
  padding: 1.5rem 0;
}

.footer-bottom-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.copyright {
  color: var(--text-light);
  font-size: 0.875rem;
  margin: 0;
}

.footer-tech {
  display: flex;
  gap: 0.5rem;
}

.tech-badge {
  background: rgba(225, 123, 71, 0.1);
  color: var(--primary-color);
  padding: 0.25rem 0.5rem;
  border-radius: var(--radius-sm);
  font-size: 0.75rem;
  font-weight: 500;
  border: 1px solid rgba(225, 123, 71, 0.2);
}

/* Responsive Design */
@media (max-width: 1024px) {
  .footer-content {
    grid-template-columns: 1fr 1fr;
    gap: 2rem;
  }
  
  .footer-brand {
    grid-column: 1 / -1;
    max-width: none;
    margin-bottom: 1rem;
  }
}

@media (max-width: 768px) {
  .footer-content {
    grid-template-columns: 1fr;
    gap: 2rem;
    text-align: center;
  }
  
  .footer-brand {
    text-align: center;
  }
  
  .footer-links {
    align-items: center;
  }
  
  .footer-bottom-content {
    flex-direction: column;
    gap: 1rem;
    text-align: center;
  }
  
  .footer-tech {
    justify-content: center;
  }
}
</style>